import React, { useCallback, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { mapInviteResult } from '@atlassian/expansion-services/services';

import { ErrorPanel, MAX_INVITES } from './error-panel';
import { InvitePanel } from './invite-panel';
import { SuccessPanel } from './success-panel';
import { type BulkInviteResult, type InlineInviteComponentProps } from './types';

export const InlineInviteComponent = ({
	emails,
	product,
	invite,
	onInviteComplete,
	onDismiss,
	continueUrl,
	resources,
	integration,
	touchpoint,
	isRecaptchaLoaded,
	getRecaptchaToken,
}: InlineInviteComponentProps): React.JSX.Element => {
	const [activePanel, setActivePanel] = useState<'invite' | 'success' | 'error'>('invite');
	const [inviteResult, setInviteResult] = useState<BulkInviteResult>({
		invited: [],
		requested: [],
		error: [],
	});
	const [exceedsMaxInvites, setExceedsMaxInvites] = useState(false);
	const [endpointFailure, setEndpointFailure] = useState(false);

	const handleInvite = useCallback(async () => {
		const recaptchaToken = await getRecaptchaToken('invite').catch(() => undefined);
		const recaptchaAction = `InlineInvite_${integration}`;

		if (emails.length > MAX_INVITES) {
			setExceedsMaxInvites(true);
			setActivePanel('error');
			return;
		}
		const users = emails.map((email) => ({ email }));
		const [response] = await Promise.all([
			invite({
				continueUrl,
				users,
				resources,
				integration,
				touchpoint,
				recaptchaOptions: { recaptchaToken, recaptchaAction },
			}),
			/* Delay to give the user enough time to read the recaptcha message */
			new Promise((resolve) => {
				setTimeout(resolve, 2500);
			}),
		]);

		let failure = false;
		if (fg('expansion-services-in-core-invites')) {
			const result = mapInviteResult(response);
			failure = !!result.failure;
		} else {
			failure = response.every((res) => res.inviteStatus === null);
		}

		if (failure) {
			setEndpointFailure(true);
			const res = { invited: [], requested: [], error: users };
			setInviteResult(res);
			onInviteComplete(res);
			setActivePanel('error');
			return;
		}

		const { invited, requested, error } = mapInviteResult(response);
		const res = {
			invited: invited.map(({ email, id }) => ({ email, id })),
			requested: requested.map(({ email, id }) => ({ email, id })),
			error: error.map(({ email }) => ({ email })),
		};
		setInviteResult(res);
		onInviteComplete(res);

		if (error.length > 0) {
			setActivePanel('error');
			return;
		}

		setActivePanel('success');
	}, [
		invite,
		continueUrl,
		emails,
		resources,
		integration,
		touchpoint,
		getRecaptchaToken,
		onInviteComplete,
	]);

	switch (activePanel) {
		case 'invite':
			return (
				<InvitePanel
					emails={emails}
					product={product}
					onInvite={handleInvite}
					onDismiss={onDismiss}
					isRecaptchaLoaded={isRecaptchaLoaded}
					integration={integration}
				/>
			);
		case 'success':
			return (
				<SuccessPanel
					invitedEmails={inviteResult.invited.map(({ email }) => email)}
					requestedEmails={inviteResult.requested.map(({ email }) => email)}
					integration={integration}
				/>
			);
		case 'error':
			return (
				<ErrorPanel
					endpointFailure={endpointFailure}
					exceedsMaxInvites={exceedsMaxInvites}
					successEmails={[
						...inviteResult.invited.map(({ email }) => email),
						...inviteResult.requested.map(({ email }) => email),
					]}
					errorEmails={inviteResult.error.map(({ email }) => email)}
					integration={integration}
				/>
			);
	}
};
